import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MessageCircle, ExternalLink, Send } from "lucide-react";

export default function WhatsAppPage() {
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [testCustomer, setTestCustomer] = useState("");
  const [testTemplate, setTestTemplate] = useState("welcome");

  const loadAll = async () => {
    const [c, t, cu, l] = await Promise.all([
      api.get("/whatsapp/config"),
      api.get("/whatsapp/templates"),
      api.get("/customers"),
      api.get("/whatsapp/logs"),
    ]);
    setConfig(c.data);
    setTemplates(t.data);
    setCustomers(cu.data);
    setLogs(l.data);
  };

  useEffect(() => { loadAll(); }, []);

  const saveConfig = async () => {
    try {
      await api.put("/whatsapp/config", config);
      toast.success("Configurazione salvata");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveTemplate = async (tpl) => {
    try {
      await api.put(`/whatsapp/templates/${tpl.key}`, tpl);
      toast.success("Template salvato");
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendTest = async () => {
    if (!testCustomer) return toast.error("Seleziona un cliente");
    try {
      const { data } = await api.post("/whatsapp/send", { customer_id: testCustomer, template_key: testTemplate });
      toast.success("Messaggio pronto");
      window.open(data.wa_link, "_blank");
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!config) return <div className="text-zinc-500">Caricamento…</div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="whatsapp-page">
      <div>
        <span className="pill" style={{ background: "rgba(37,211,102,0.15)", color: "#4ADE80", border: "1px solid rgba(37,211,102,0.3)" }}>
          <MessageCircle className="h-3 w-3" /> WHATSAPP HOOK
        </span>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Comunica coi tuoi clienti</h1>
        <p className="text-zinc-400 mt-1">
          Modalità link wa.me pronta oggi. Configura un provider (Twilio / Meta Cloud) quando vuoi invii automatici.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-elev p-5 space-y-4">
          <h3 className="font-heading font-semibold text-lg">Provider</h3>
          <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-950 border border-zinc-800">
            <div><div className="text-sm font-medium">Invio automatico</div>
              <div className="text-xs text-zinc-500">Se OFF si usa link wa.me</div></div>
            <Switch data-testid="wa-enabled" checked={config.enabled} onCheckedChange={(v) => setConfig({ ...config, enabled: v })} />
          </div>
          <div><Label className="text-zinc-300 mb-1 inline-block">Provider</Label>
            <Select value={config.provider} onValueChange={(v) => setConfig({ ...config, provider: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                <SelectItem value="twilio">Twilio</SelectItem>
                <SelectItem value="meta">Meta Cloud API</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-zinc-300 mb-1 inline-block">Account SID / API Key</Label>
            <Input data-testid="wa-sid" value={config.account_sid} onChange={(e) => setConfig({ ...config, account_sid: e.target.value })} className="bg-zinc-950 border-zinc-800" />
          </div>
          <div><Label className="text-zinc-300 mb-1 inline-block">Auth token</Label>
            <Input data-testid="wa-token" type="password" value={config.auth_token} onChange={(e) => setConfig({ ...config, auth_token: e.target.value })} className="bg-zinc-950 border-zinc-800" />
          </div>
          <div><Label className="text-zinc-300 mb-1 inline-block">Numero mittente</Label>
            <Input data-testid="wa-from" value={config.from_number} onChange={(e) => setConfig({ ...config, from_number: e.target.value })} placeholder="+39…" className="bg-zinc-950 border-zinc-800" />
          </div>
          <Button data-testid="wa-config-save" onClick={saveConfig} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">Salva configurazione</Button>
        </div>

        <div className="card-elev p-5 lg:col-span-2 space-y-4">
          <h3 className="font-heading font-semibold text-lg">Template messaggi</h3>
          <p className="text-xs text-zinc-500">Variabili disponibili: <span className="font-mono">{"{{name}}"}</span>, <span className="font-mono">{"{{points}}"}</span>, <span className="font-mono">{"{{card}}"}</span></p>
          <div className="space-y-3">
            {templates.map((t, idx) => (
              <div key={t.key} className="p-4 rounded-xl bg-zinc-950 border border-zinc-800 space-y-2" data-testid={`wa-tpl-${t.key}`}>
                <div className="flex items-center justify-between">
                  <div className="font-heading font-semibold">{t.label}</div>
                  <span className="pill pill-muted font-mono">{t.key}</span>
                </div>
                <Textarea
                  data-testid={`wa-tpl-body-${t.key}`}
                  value={t.body}
                  onChange={(e) => {
                    const copy = [...templates]; copy[idx].body = e.target.value; setTemplates(copy);
                  }}
                  className="bg-black border-zinc-800 min-h-[80px]"
                />
                <div className="flex justify-end">
                  <Button data-testid={`wa-tpl-save-${t.key}`} size="sm" onClick={() => saveTemplate(t)} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
                    Salva
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-800 space-y-3">
            <div className="font-heading font-semibold flex items-center gap-2"><Send className="h-4 w-4" /> Invio di prova</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Select value={testCustomer} onValueChange={setTestCustomer}>
                <SelectTrigger data-testid="wa-test-customer" className="bg-black border-zinc-800"><SelectValue placeholder="Cliente" /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 max-h-64">
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={testTemplate} onValueChange={setTestTemplate}>
                <SelectTrigger data-testid="wa-test-template" className="bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button data-testid="wa-test-send" onClick={sendTest} className="bg-[color:var(--whatsapp)] text-black hover:opacity-90 font-semibold">
                <ExternalLink className="h-4 w-4 mr-1" /> Genera link
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="card-elev p-5">
        <h3 className="font-heading font-semibold text-lg mb-3">Ultimi invii</h3>
        <ul className="space-y-2 max-h-80 overflow-y-auto">
          {logs.length === 0 && <li className="text-sm text-zinc-500">Nessun invio ancora.</li>}
          {logs.map((l) => (
            <li key={l.id} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <div className="text-sm">{l.message}</div>
                <div className="text-[11px] text-zinc-500">{new Date(l.created_at).toLocaleString("it-IT")} · {l.template_key} · via {l.delivered_via}</div>
              </div>
              <a href={l.wa_link} target="_blank" rel="noreferrer" className="pill pill-mint hover:opacity-90">Apri</a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
